import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';

import { clearAllOfflineData } from 'common/offline/reactQueryPersistence';
import { clearAllFileUploads } from 'common/offline/fileStorage';
import { clearAllLocalPhotos } from 'common/offline/offlinePhotoStorage';
import { clearMutationQueue } from 'common/offline/mutationQueue';

export const useClearCache = () => {
  const queryClient = useQueryClient();
  const [isClearing, setIsClearing] = useState(false);

  const clearCache = useCallback(async () => {
    setIsClearing(true);
    try {
      // Clear all persisted data: queries, mutations, and file uploads
      const results = await Promise.allSettled([clearAllOfflineData(), clearMutationQueue(), clearAllFileUploads()]);

      clearAllLocalPhotos();

      try {
        await caches.delete('images');
      } catch {
        // Cache API may not be available in all environments
      }

      // Remove all in-memory queries and mutations without triggering re-fetches.
      // removeQueries() removes entries silently — unlike resetQueries() which
      // triggers re-fetches for active observers, causing unwanted network requests.
      queryClient.removeQueries();
      queryClient.getMutationCache().clear();

      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length > 0) {
        throw failures[0].reason;
      }
    } finally {
      setIsClearing(false);
    }
  }, [queryClient]);

  return { clearCache, isClearing };
};
